// WebSocket-relayed voice chat.
//
// This is the "dumb" replacement for WebRTC. Instead of establishing a
// peer-to-peer audio stream (which requires ICE/STUN/TURN negotiation and
// fails for users behind strict NATs), we:
//
//   • Capture the mic via Web Audio, downsample to 8 kHz mono Int16 PCM,
//     package it into 20 ms frames (160 samples = 320 bytes), and send
//     each frame as a base64-encoded string over the existing game WS.
//   • On the receive side, we decode the base64 back to Int16, convert to
//     Float32, wrap in an AudioBuffer, and schedule it on an AudioContext
//     so frames from the same speaker play back-to-back with no gaps.
//
// Pros:
//   • Works 100% of the time the WS is connected (it's the same pipe as
//     dice rolls). No NAT, no CSP, no browser WebRTC quirks.
//   • No TURN-server dependency or cost.
//   • Same latency envelope as the WS itself (~30–100 ms typical).
//
// Cons:
//   • Server bandwidth ~16 kB/s per active speaker (vs ~4 kB/s for WebRTC
//     Opus). Fine for a Ludo game; not fine for a 1000-user voice chat.
//   • Audio is not peer-to-peer encrypted (still TLS via wss://).
//
// Mic gesture: you MUST call prewarmMic() inside a real user click (e.g.
// the Play button) so Safari / strict mobile browsers grant mic access.
// start() will try again if prewarmMic wasn't done, but it might be
// rejected on those browsers.

type Color = 'red' | 'green' | 'yellow' | 'blue';

// Sample rate the server sees. 8 kHz is telephone-grade quality —
// plenty clear for voice, tiny packet size. If you want music-quality
// later, bump to 16 kHz (packets double in size).
const WIRE_SAMPLE_RATE = 8000;
// 20 ms frames at 8 kHz = 160 samples = 320 bytes of Int16.
const FRAME_SAMPLES = 160;

type SendFn = (msg: { type: 'voice_chunk'; pcm: string }) => void;

export class WSVoice {
  private ownColor: Color;
  private send: SendFn;

  private micStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private captureNode: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  // Buffer samples from the browser's native sample rate (usually 48 kHz)
  // until we've collected enough to downsample into one 160-sample frame.
  private sampleQueue: Float32Array[] = [];
  private sampleQueueLen = 0;
  private browserSampleRate = 48000;

  private playbackCtx: AudioContext | null = null;
  // Per-remote scheduler. nextStartTime is the AudioContext timestamp at
  // which the NEXT chunk should start playing. Keeps frames gapless.
  private nextStartTime = new Map<Color, number>();

  private muted = false;
  private listenMuted = false;

  constructor(ownColor: Color, send: SendFn) {
    this.ownColor = ownColor;
    this.send = send;
  }

  // Acquire the mic. Call inside a user-gesture click handler if possible
  // — browsers grant/deny based on whether you're in a gesture context.
  static async prewarmMic(): Promise<MediaStream | null> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      (window as any).__slMicStream = s;
      return s;
    } catch (e) {
      console.warn('[sl wsVoice] prewarmMic denied', e);
      return null;
    }
  }

  async start(): Promise<boolean> {
    try {
      const warm = (window as any).__slMicStream as MediaStream | undefined;
      if (warm && warm.getAudioTracks().some((t) => t.readyState === 'live')) {
        this.micStream = warm;
        (window as any).__slMicStream = null;
      } else {
        this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
    } catch (e) {
      console.warn('[sl wsVoice] mic denied', e);
      return false;
    }

    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    this.captureCtx = new Ctx();
    this.browserSampleRate = this.captureCtx.sampleRate;
    this.sourceNode = this.captureCtx.createMediaStreamSource(this.micStream);
    // ScriptProcessorNode is deprecated but universally supported and
    // perfectly fine for 8 kHz voice. The buffer size affects frame
    // latency — 2048 @ 48 kHz = 43 ms of audio per callback.
    this.captureNode = this.captureCtx.createScriptProcessor(2048, 1, 1);
    this.sourceNode.connect(this.captureNode);
    // Must connect to destination or some browsers stop firing callbacks.
    // We use a gain of 0 so the user doesn't hear their own mic.
    const silent = this.captureCtx.createGain();
    silent.gain.value = 0;
    this.captureNode.connect(silent);
    silent.connect(this.captureCtx.destination);

    this.captureNode.onaudioprocess = (ev) => {
      if (this.muted) return;
      const input = ev.inputBuffer.getChannelData(0);
      // Copy out — the backing buffer is reused next callback.
      const copy = new Float32Array(input.length);
      copy.set(input);
      this.sampleQueue.push(copy);
      this.sampleQueueLen += copy.length;
      this.drainToFrames();
    };

    // Playback context — separate from capture so their sample rates
    // don't have to match. Kept at browser default for best fidelity
    // on the receive side.
    this.playbackCtx = new Ctx();
    // Safari requires the context to be resumed after a user gesture.
    if (this.playbackCtx.state === 'suspended') {
      try { await this.playbackCtx.resume(); } catch {}
    }
    console.info('[sl wsVoice] started, browserRate=', this.browserSampleRate, 'wire=', WIRE_SAMPLE_RATE);
    return true;
  }

  // Drain the queue: whenever we have enough raw samples to produce one
  // 160-sample frame at WIRE_SAMPLE_RATE, do it and ship the frame.
  private drainToFrames() {
    const ratio = this.browserSampleRate / WIRE_SAMPLE_RATE;
    const rawNeeded = Math.ceil(FRAME_SAMPLES * ratio);
    while (this.sampleQueueLen >= rawNeeded) {
      // Flatten the first rawNeeded samples out of the queue.
      const raw = new Float32Array(rawNeeded);
      let filled = 0;
      while (filled < rawNeeded && this.sampleQueue.length) {
        const head = this.sampleQueue[0];
        const take = Math.min(rawNeeded - filled, head.length);
        raw.set(head.subarray(0, take), filled);
        filled += take;
        if (take === head.length) this.sampleQueue.shift();
        else this.sampleQueue[0] = head.subarray(take);
      }
      this.sampleQueueLen -= rawNeeded;

      // Downsample: take every Nth sample (cheap, works for voice).
      const frame = new Int16Array(FRAME_SAMPLES);
      for (let i = 0; i < FRAME_SAMPLES; i++) {
        const src = raw[Math.floor(i * ratio)] || 0;
        const clamped = Math.max(-1, Math.min(1, src));
        frame[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      }

      // Encode as base64 and send.
      const b64 = int16ToBase64(frame);
      try { this.send({ type: 'voice_chunk', pcm: b64 }); } catch {}
    }
  }

  // Called by Ludo.tsx when a voice_chunk message arrives from a remote.
  onRemoteChunk(from: Color, b64: string) {
    if (from === this.ownColor) return;
    if (this.listenMuted) return;
    if (!this.playbackCtx) return;
    const frame = base64ToInt16(b64);
    if (!frame || frame.length === 0) return;
    // Convert Int16 → Float32 in [-1, 1] and stuff into an AudioBuffer at
    // the WIRE sample rate. The browser's AudioContext handles resampling
    // for us automatically — no manual upsampling needed.
    const f32 = new Float32Array(frame.length);
    for (let i = 0; i < frame.length; i++) f32[i] = frame[i] / 0x8000;
    const buf = this.playbackCtx.createBuffer(1, frame.length, WIRE_SAMPLE_RATE);
    buf.copyToChannel(f32, 0);
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playbackCtx.destination);

    // Schedule back-to-back. If we fell behind (playback time passed the
    // next start time already), reset to "now + small jitter buffer".
    const now = this.playbackCtx.currentTime;
    let at = this.nextStartTime.get(from) ?? now + 0.05;
    if (at < now) at = now + 0.02;
    src.start(at);
    this.nextStartTime.set(from, at + buf.duration);
  }

  setMuted(m: boolean) { this.muted = m; }
  isMuted() { return this.muted; }
  setListenMuted(m: boolean) { this.listenMuted = m; }
  isListenMuted() { return this.listenMuted; }

  hasLocalStream() {
    return !!this.micStream && this.micStream.getAudioTracks().some((t) => t.readyState === 'live');
  }

  // Kept for API compatibility with the old VoiceMesh — no-op in WS mode
  // (there are no peers; the server relays everything).
  onPeers(_peers: Color[]) {}
  onSignal(_from: Color, _payload: any) {}
  dropPeer(_color: Color) {}
  // Fallback path used by the mic-toggle button when start() failed
  // (permission denied the first time, then the user tapped unmute
  // to retry with an explicit gesture). Re-runs the full capture setup
  // with the freshly-granted stream.
  async attachLocalStream(stream: MediaStream) {
    (window as any).__slMicStream = stream;
    await this.start();
  }

  // After a listen-mute toggle, Safari can leave the playback context
  // suspended. Nudge it back to life on toggle-off.
  kickAudioPlayback() {
    if (this.playbackCtx && this.playbackCtx.state === 'suspended') {
      this.playbackCtx.resume().catch(() => {});
    }
  }

  stop() {
    try { this.captureNode?.disconnect(); } catch {}
    try { this.sourceNode?.disconnect(); } catch {}
    try { this.captureCtx?.close(); } catch {}
    try { this.playbackCtx?.close(); } catch {}
    this.captureCtx = null;
    this.playbackCtx = null;
    this.captureNode = null;
    this.sourceNode = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.sampleQueue = [];
    this.sampleQueueLen = 0;
    this.nextStartTime.clear();
  }
}

// ─── tiny base64 helpers for Int16 PCM frames ────────────────────────────
// We ship audio as JSON over the existing WS so a base64 wrapper keeps
// the transport identical to every other message. 320 bytes of Int16
// encodes to ~428 chars of base64 — still under 0.5 KB per frame.

function int16ToBase64(arr: Int16Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return btoa(s);
}

function base64ToInt16(b64: string): Int16Array | null {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    // Must be aligned to 2-byte boundary for Int16.
    if (bytes.length % 2 !== 0) return null;
    return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.length / 2);
  } catch {
    return null;
  }
}
